import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonSize, ButtonType, DateRangeInput, Dropdown, Icon, IconSize, IDropdownItem, Input, Tag, Tags} from "@renta-apps/renta-react-components";
import Localizer from "@/localization/Localizer";
import styles from "./FiltersAndSort.module.scss";
import {useLocalStorage, useResize} from "@/helpers/Hooks";
import {searchProductGroupNames} from "@/services/ProductService";
import {ProductGroupName} from "@/models/server/ProductGroupName";
import {getMostPopularSearchesAsync, saveSearchLogAsync} from "@/services/SearchLogsService";
import {SearchType} from "@/models/Enums";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";
import {ConstructionSiteEquipmentFilters} from "@/models/server/ConstructionSiteEquipmentFilters";
import {getFiltersForConstructionSite} from "@/services/ConstructionSiteService";
import LocalizationHelper from "@/helpers/LocalizationHelper";
import {formatDate} from "@/helpers/DateHelper";
import {IFiltersAndSortProps} from "@/pages/ConstructionSiteDetails/EquipmentsNew/FiltersAndSort/FiltersAndSort";

interface IFiltersFormProps extends IFiltersAndSortProps {
    filtersModalVisible?: boolean;
    onFiltersModalVisibilityChange?: (visible: boolean) => void;
    idPrefix?: string;
    modalMode?: boolean;
}

enum FilterType {
    DEVICE_NAMES = "device_names",
    PRODUCT_GROUPS = "product_groups",
    STATUSES = "statuses",
    CATEGORIES = "categories",
    DATE_RANGE = "date_range",
    RENTERS = "renters",
    CONTRACTS = "contracts",
    DEPOTS = "depots",
}

const MODAL_MAX_WIDTH = 576;

const dateToString = (date?: Date): string => {
    return date ? formatDate(new Date(date), true, false)! : '##.##.####';
};

const FiltersForm: React.FC<IFiltersFormProps> = ({constructionSiteId, params, onParamValueChange, onFiltersModalVisibilityChange, filtersModalVisible, modalMode = false, idPrefix = ''}) => {
    const [deviceName, setDeviceName] = useState<string>('');
    const [productGroup, setProductGroup] = useState<string>('');
    const [additionalFilterValues, setAdditionalFilterValues] = useState<ConstructionSiteEquipmentFilters | null>(null);
    const [popularProductGroups, setPopularProductGroups] = useState<IDropdownItem[]>([]);
    const [categoriesList, setCategoriesList] = useState<IDropdownItem[]>([]);
    const [rentersList, setRentersList] = useState<IDropdownItem[]>([]);
    const [contractsList, setContractsList] = useState<IDropdownItem[]>([]);
    const [depotsList, setDepotsList] = useState<IDropdownItem[]>([]);
    const [deviceNameSuggestions, setDeviceNameSuggestions] = useLocalStorage<string[]>("renta.equipments.deviceName.suggestions", []);
    const [productGroupSuggestions, setProductGroupSuggestions] = useLocalStorage<string[]>("renta.equipments.productGroup.suggestions", []);
    const [combinedFilterValues, setCombinedFilterValues] = useState<Tag[]>([]);
    const [productGroupNames, setProductGroupNames] = useState<ProductGroupName[]>([]);
    const [additionalFiltersVisible, setAdditionalFiltersVisible] = useState(modalMode);
    const {isMobile, windowWidth} = useResize(200);

    const hasTags = useMemo(() => combinedFilterValues.length > 0, [combinedFilterValues]);

    const statuses: IDropdownItem[] = useMemo(() => {
        return params.statuses.map(item => ({value: item.id, name: item.name || ''}));
    }, [params.statuses]);

    const categories: IDropdownItem[] = useMemo(() => {
        return params.categories.map(item => ({value: item.id, name: item.name || ''}));
    }, [params.categories]);

    const renters: IDropdownItem[] = useMemo(() => {
        return params.renters.map(item => ({value: item, name: item || ''}));
    }, [params.renters]);

    const contracts: IDropdownItem[] = useMemo(() => {
        return params.contracts.map(item => ({value: item, name: item || ''}));
    }, [params.contracts]);

    const depots: IDropdownItem[] = useMemo(() => {
        return params.depots.map(item => ({value: item.id, name: item.name || ''}));
    }, [params.depots]);

    useEffect(() => {
        setCombinedFilterValues([
            ...params.deviceNames.map((item) => (
                {id: item, label: item ?? '', tooltip: Localizer.fleetMonitoringPageFiltersTooltipNameOrId, type: FilterType.DEVICE_NAMES}
            )),
            ...params.productGroups.map((item) => (
                {id: item.id ?? '', label: item.name ?? '', tooltip: Localizer.fleetMonitoringPageGridProductGroup, type: FilterType.PRODUCT_GROUPS}
            )),
            ...params.statuses.map((item) => (
                {id: item.id ?? '', label: item.name ?? '', tooltip: Localizer.constructionSiteDetailsEquipmentTabStatus, type: FilterType.STATUSES}
            )),
            ...params.categories.map((item) => (
                {id: item.id ?? '', label: item.name ?? '', tooltip: Localizer.constructionSiteDetailsEquipmentTabCategory, type: FilterType.CATEGORIES}
            )),
            ...params.rentalPeriods.map((item, index) => (
                {id: `date-range-${index}`, label: `${dateToString(item.startDate)} - ${dateToString(item.endDate)}`, tooltip: Localizer.constructionSiteDetailsEquipmentTabRentalPeriod, type: FilterType.DATE_RANGE}
            )),
            ...params.renters.map((item) => (
                {id: item, label: item ?? '', tooltip: Localizer.constructionSiteDetailsEquipmentTabRentedBy, type: FilterType.RENTERS}
            )),
            ...params.contracts.map((item) => (
                {id: item, label: item ?? '', tooltip: Localizer.constructionSiteDetailsEquipmentTabContract, type: FilterType.CONTRACTS}
            )),
            ...params.depots.map((item) => (
                {id: item.id ?? '', label: item.name ?? '', tooltip: Localizer.constructionSiteDetailsEquipmentTabDepot, type: FilterType.DEPOTS}
            )),
        ]);
    }, [params.deviceNames, params.productGroups, params.statuses, params.categories, params.rentalPeriods, params.renters, params.contracts, params.depots]);

    useEffect(() => {
        if (productGroupSuggestions.length) {
            return;
        }

        loadPopularProductGroups().catch();
    }, [productGroupSuggestions]);

    useEffect(() => {
        if (filtersModalVisible && windowWidth >= MODAL_MAX_WIDTH) {
            onFiltersModalVisibilityChange?.(false);
            setAdditionalFiltersVisible(true);
        } else if (!modalMode && additionalFiltersVisible && windowWidth < MODAL_MAX_WIDTH) {
            setAdditionalFiltersVisible(false);
            onFiltersModalVisibilityChange?.(true);
        }
    }, [windowWidth]);

    useEffect(() => {
        if (!additionalFiltersVisible || !!additionalFilterValues) {
            return;
        }

        loadAdditionalFilterValues().catch();
    }, [additionalFiltersVisible]);

    const productGroupsSuggestionsDelayMs = useMemo(() => {
        return productGroup.length < 3 ? 0 : 300;
    }, [productGroup]);

    const filterByStatusItems: IDropdownItem[] = [
        {name: Localizer.constructionSiteDetailsEquipmentTabStatusInRent, value: RentaEasyConstants.ConstructionSiteEquipmentConstants.statusInRent},
        {name: Localizer.constructionSiteDetailsEquipmentTabStatusReturned, value: RentaEasyConstants.ConstructionSiteEquipmentConstants.statusReturned},
        {name: Localizer.constructionSiteDetailsEquipmentTabStatusReturnRequested, value: RentaEasyConstants.ConstructionSiteEquipmentConstants.statusReturnRequested},
    ];

    const onFiltersReset = () => {
        onParamValueChange({deviceNames: [], productGroups: [], statuses: [], categories: [], rentalPeriods: [], renters: [], contracts: [], depots: []});
    };

    const toggleDeviceNamesItem = (label: string) => {
        if (params.deviceNames.some((name) => name === label)) {
            onParamValueChange({deviceNames: params.deviceNames.filter((name) => name !== label)});
        } else {
            onParamValueChange({deviceNames: [...params.deviceNames, label]});
        }
    };

    const toggleProductGroupFilterItem = (label: string, id?: string) => {
        if (params.productGroups.some((group) => group.name === label)) {
            onParamValueChange({productGroups: params.productGroups.filter((group) => group.name !== label)});
        } else {
            onParamValueChange({productGroups: [...params.productGroups, {id: id, name: label}]});
        }
    };

    const toggleStatusFilterItem = (label: string, id?: string) => {
        if (params.statuses.some((status) => status.name === label)) {
            onParamValueChange({statuses: params.statuses.filter((status) => status.name !== label)});
        } else {
            onParamValueChange({statuses: [...params.statuses, {id: id, name: label}]});
        }
    };

    const toggleCategoryFilterItem = (label: string, id?: string) => {
        if (params.categories.some((category) => category.name === label)) {
            onParamValueChange({categories: params.categories.filter((category) => category.name !== label)});
        } else {
            onParamValueChange({categories: [...params.categories, {id: id, name: label}]});
        }
    };

    const toggleFilterItem = (tag: Tag) => {
        const tagType = tag.type;
        const tagId = tag.id;
        const tagName = tag.label;
        switch (tagType) {
            case FilterType.DEVICE_NAMES:
                toggleDeviceNamesItem(tagName);
                break;
            case FilterType.PRODUCT_GROUPS:
                toggleProductGroupFilterItem(tagName, tagId);
                break;
            case FilterType.STATUSES:
                toggleStatusFilterItem(tagName, tagId);
                break;
            case FilterType.CATEGORIES:
                toggleCategoryFilterItem(tagName, tagId);
                break;
            case FilterType.DATE_RANGE:
                onParamValueChange({rentalPeriods: params.rentalPeriods.filter((_, index) => `date-range-${index}` !== tagId)});
                break;
            case FilterType.RENTERS:
                onParamValueChange({renters: params.renters.filter((renter) => renter !== tagName)});
                break;
            case FilterType.CONTRACTS:
                onParamValueChange({contracts: params.contracts.filter((contract) => contract !== tagName)});
                break;
            case FilterType.DEPOTS:
                onParamValueChange({depots: params.depots.filter((depot) => depot.id !== tagId)});
                break;
            default:
                console.error(`Unknown tag type: ${tagType}`);
        }
    };

    const loadPopularProductGroups = async () => {
        try {
            const response = await getMostPopularSearchesAsync(SearchType.ProductGroup);
            setPopularProductGroups(response.map((group) => ({name: group.name, value: group.externalId})));
            setProductGroupNames(response);
        } catch (error) {
            console.error("Error loading popular product groups:", error);
        }
    };

    const loadProductGroupSuggestions = async (searchTerm: string) : Promise<IDropdownItem[]> => {
        try {
            const response = await searchProductGroupNames(searchTerm);
            setProductGroupNames(response);
            return (response ?? []).map((group) => (
                {name: `${group.name} (${group.externalId})`, value: group.externalId}
            ));
        } catch (error) {
            console.error("Error loading product group suggestions:", error);
            return [];
        }
    };

    const formatSelectedLabel = (selectedCount: number) => {
        return selectedCount ? Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, selectedCount) : undefined;
    };

    const getDeviceNameSuggestions = useCallback((value: string) => {
        return value ? [] : deviceNameSuggestions.map(item => ({name: item, value: item}));
    }, [deviceNameSuggestions]);

    const getProductGroupSuggestions = useCallback(async (value: string) : Promise<IDropdownItem[]> => {
        const searchTerm = value.trim();
        if (searchTerm.length >= 3) {
            return await loadProductGroupSuggestions(searchTerm);
        }

        if (value) {
            return [];
        }

        return productGroupSuggestions.length ? productGroupSuggestions.map(item => ({name: item, value: item})) : popularProductGroups;
    }, [productGroupSuggestions, popularProductGroups]);

    const setProductGroupsLocal = (item: IDropdownItem) => {
        if (params.productGroups.some((group) => group.id === item.value && group.name === item.name)) {
            return;
        }

        if (item.value) {
            const productGroupName = productGroupNames.find(group => group.externalId === item.value);
            if (productGroupName) {
                saveSearchLogAsync(SearchType.ProductGroup, productGroupName.id).catch();
            }
        }

        onParamValueChange({productGroups: [...params.productGroups, {id: item.value ? item.value.toString() : undefined, name: item.name}]});
    };

    const handleProductGroupSuggestionClicked = (item: IDropdownItem) => {
        setProductGroup('');
        let selectedItem = item;
        if (item.name === item.value) {
            selectedItem = {name: item.name};
        }

        setProductGroupsLocal(selectedItem);
    };

    const loadAdditionalFilterValues = async () => {
        try {
            const response = await getFiltersForConstructionSite(constructionSiteId);
            setAdditionalFilterValues(response);
            setCategoriesList(response.categories
                .sort((a, b) => a.name!.localeCompare(b.name!, Localizer.language))
                .map((category) => ({name: category.name, value: category.id})));
            setRentersList(response.renters.map((renter) => ({name: renter, value: renter})));
            setContractsList(response.contracts.map((contract => ({name: contract, value: contract}))));
            setDepotsList(response.depots.map((depot) => ({name: depot.name || Localizer.genericNameUnknown, value: depot.id})));
        } catch (error) {
            console.error("Error loading additional filter values:", error);
        }
    };

    const searchCategoriesNames = (value: string) => {
        if (!additionalFilterValues) {
            setCategoriesList([]);
            return;
        }
        const searchTerm = value.trim().toLowerCase();
        setCategoriesList(additionalFilterValues.categories
            .filter((category) => category.name.toLowerCase().includes(searchTerm))
            .map((category) => ({name: category.name, value: category.id})));
    };

    const searchDepotNames = (value: string) => {
        if (!additionalFilterValues) {
            setCategoriesList([]);
            return;
        }
        const searchTerm = value.trim().toLowerCase();
        setDepotsList(additionalFilterValues.depots
            .map((depot) => ({name: depot.name || Localizer.genericNameUnknown, value: depot.id}))
            .filter((depot) => depot.name.toLowerCase().includes(searchTerm)));
    };

    const handleMoreFiltersButtonClick = () => {
        if (windowWidth < MODAL_MAX_WIDTH) {
            onFiltersModalVisibilityChange?.(true);
        } else {
            setAdditionalFiltersVisible(prev => !prev);
        }
    };

    const handleFormSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const trimmedDeviceName = deviceName.trim();
        if (trimmedDeviceName) {
            setDeviceNameSuggestions(prev => [trimmedDeviceName, ...prev.filter(item => item !== trimmedDeviceName)].slice(0, 5));
            onParamValueChange({deviceNames: [...(new Set([...params.deviceNames, trimmedDeviceName]))]});
            setDeviceName('');
        }

        const trimmedProductGroup = productGroup.trim();
        if (trimmedProductGroup) {
            setProductGroupSuggestions(prev => [trimmedProductGroup, ...prev.filter(item => item !== trimmedProductGroup)].slice(0, 5));
            setProductGroupsLocal({value: undefined, name: trimmedProductGroup});
            setProductGroup('');
        }
        (document.activeElement as HTMLInputElement)?.blur();
    };

    return (
        <>
            <form className={`${styles.filtersContainer} ${modalMode ? styles.modalForm : ''}`} onSubmit={handleFormSubmit}>
                <div className={styles.searchRow}>
                    <div className={styles.deviceName}>
                        <Input id={`${idPrefix}filter-equipments-by-name`}
                               value={deviceName}
                               label={Localizer.fleetMonitoringPageFiltersLabelsName}
                               inputProperties={{
                                   name: 'deviceName',
                                   placeholder: formatSelectedLabel(params.deviceNames.length),
                                   autoComplete: 'off'
                               }}
                               onChange={setDeviceName}
                               useSuggestions
                               minValueLengthForSuggestions={0}
                               getSuggestionsDelayMs={0}
                               getSuggestions={(value: string) => getDeviceNameSuggestions(value)}
                               onSuggestionClick={(item: IDropdownItem) => {
                                   setDeviceName('');
                                   onParamValueChange({deviceNames: [...(new Set([...params.deviceNames, item.value!.toString()]))]});
                               }}
                               onBlur={() => modalMode && handleFormSubmit()}
                        />
                    </div>
                    <div className={styles.productGroup}>
                        <Input id={`${idPrefix}filter-equipments-by-product-group`}
                               value={productGroup}
                               label={Localizer.constructionSiteDetailsEquipmentTabProductGroup}
                               inputProperties={{
                                   name: 'productGroup',
                                   placeholder: formatSelectedLabel(params.productGroups.length),
                                   autoComplete: 'off'
                               }}
                               onChange={setProductGroup}
                               useSuggestions
                               minValueLengthForSuggestions={0}
                               getSuggestionsDelayMs={productGroupsSuggestionsDelayMs}
                               getSuggestions={async (value: string) => await getProductGroupSuggestions(value)}
                               onSuggestionClick={handleProductGroupSuggestionClicked}
                               data-cy="filter-equipments-by-product-group"
                               onBlur={() => modalMode && handleFormSubmit()}
                        />
                    </div>
                    {!modalMode && (
                        <div className={styles.search}>
                            {isMobile ? (
                                <Button className={styles.buttonWithIcon}
                                        type={ButtonType.Secondary}
                                        size={ButtonSize.Medium}
                                        id="toggle-additional-filters-button"
                                        iconLeft={{name: "sliders", color: "#FE5000", size: IconSize.Medium}}
                                        onClick={handleMoreFiltersButtonClick}
                                >
                                    {Localizer.constructionSiteDetailsEquipmentTabMoreFilters}
                                </Button>
                            ) : (
                                <Button className={styles.iconButton}
                                        type={ButtonType.Icon}
                                        size={ButtonSize.Medium}
                                        id="toggle-additional-filters-button"
                                        onClick={handleMoreFiltersButtonClick}
                                >
                                    <Icon name="sliders" color="#FE5000" size={IconSize.Medium}/>
                                </Button>
                            )}
                            <Button className={styles.buttonWithIcon}
                                    type={ButtonType.Primary}
                                    size={ButtonSize.Medium}
                                    id="search-equipments-button"
                                    iconLeft={{name: "magnifyingGlass", color: "white", size: IconSize.Medium}}
                                    submit
                            >
                                {Localizer.searchTitle}
                            </Button>
                        </div>
                    )}
                </div>
                {additionalFiltersVisible ? (
                    <div className={styles.additionalFiltersContainer} data-cy="additional-filters-container">
                        <Dropdown id={`${idPrefix}filter-equipments-by-statuses`}
                                  className={styles.formField}
                                  items={filterByStatusItems}
                                  label={Localizer.constructionSiteDetailsEquipmentTabStatus}
                                  selectedItem={statuses}
                                  onChange={(items: IDropdownItem[]) => onParamValueChange({statuses: items.map((item) => ({id: item.value!.toString(), name: item.name}))})}
                                  multiSelect
                                  allowEmpty
                                  formatSelectedLabel={formatSelectedLabel}
                                  placeHolder={Localizer.constructionSiteDetailsEquipmentTabDropDownPlaceHolder}
                        />
                        <Dropdown id={`${idPrefix}filter-equipments-by-categories`}
                                  className={styles.formField}
                                  items={categoriesList}
                                  label={Localizer.constructionSiteDetailsEquipmentTabCategory}
                                  selectedItem={categories}
                                  onChange={(items: IDropdownItem[]) => onParamValueChange({categories: items.map((item) => ({id: item.value!.toString(), name: item.name}))})}
                                  onSearch={searchCategoriesNames}
                                  multiSelect
                                  allowEmpty
                                  searchable
                                  formatSelectedLabel={formatSelectedLabel}
                                  disabled={additionalFilterValues === null}
                                  placeHolder={Localizer.constructionSiteDetailsEquipmentTabDropDownPlaceHolder}
                        />
                        <DateRangeInput isMobile={isMobile}
                                        className={styles.rentalPeriod}
                                        id={`${idPrefix}filter-equipments-by-rental-periods`}
                                        label={Localizer.constructionSiteDetailsEquipmentTabRentalPeriod}
                                        datePickerPosition="right"
                                        locale={LocalizationHelper.currentLocale}
                                        localizer={(key: string) => Localizer.get(key)}
                                        resetOnClose
                                        onChange={(startDate?: Date, endDate?: Date) => {onParamValueChange({rentalPeriods: [...params.rentalPeriods, {startDate: startDate, endDate: endDate}]})}}
                                        inputProperties={{
                                            placeholder: formatSelectedLabel(params.rentalPeriods.length),
                                        }}
                        />
                        <Dropdown id={`${idPrefix}filter-equipments-by-renters`}
                                  className={styles.formField}
                                  items={rentersList}
                                  label={Localizer.constructionSiteDetailsEquipmentTabRentedBy}
                                  selectedItem={renters}
                                  onChange={(items: IDropdownItem[]) => onParamValueChange({renters: items.map((item) => item.name)})}
                                  multiSelect
                                  allowEmpty
                                  searchable
                                  formatSelectedLabel={formatSelectedLabel}
                                  disabled={additionalFilterValues === null}
                                  placeHolder={Localizer.constructionSiteDetailsEquipmentTabDropDownPlaceHolder}
                        />
                        <Dropdown id={`${idPrefix}filter-equipments-by-contracts`}
                                  className={styles.formField}
                                  items={contractsList}
                                  label={Localizer.constructionSiteDetailsEquipmentTabContract}
                                  selectedItem={contracts}
                                  onChange={(items: IDropdownItem[]) => onParamValueChange({contracts: items.map((item) => item.name)})}
                                  multiSelect
                                  allowEmpty
                                  searchable
                                  formatSelectedLabel={formatSelectedLabel}
                                  disabled={additionalFilterValues === null}
                                  placeHolder={Localizer.constructionSiteDetailsEquipmentTabDropDownPlaceHolder}
                        />
                        <Dropdown id={`${idPrefix}filter-equipments-by-depots`}
                                  className={styles.formField}
                                  items={depotsList}
                                  label={Localizer.constructionSiteDetailsEquipmentTabDepot}
                                  selectedItem={depots}
                                  onChange={(items: IDropdownItem[]) => onParamValueChange({depots: items.map((item) => ({id: item.value!.toString(), name: item.name}))})}
                                  onSearch={searchDepotNames}
                                  multiSelect
                                  allowEmpty
                                  searchable
                                  formatSelectedLabel={formatSelectedLabel}
                                  disabled={additionalFilterValues === null}
                                  placeHolder={Localizer.constructionSiteDetailsEquipmentTabDropDownPlaceHolder}
                        />
                    </div>
                ) : null}
            </form>
            {hasTags && (
                <div className={styles.filterTags}>
                    {combinedFilterValues.length ? (
                        <Tags className={styles.tagsWrapper}
                              items={combinedFilterValues}
                              onClick={(tag: Tag) => toggleFilterItem(tag)}
                              onDelete={(tag: Tag) => toggleFilterItem(tag)}
                              onDeleteAll={onFiltersReset}
                              localizations={{deleteAllTagsTitle: Localizer.fleetMonitoringPageFiltersTagsRemoveAll}}
                              data-cy={`${idPrefix}tagsContainer`}
                        />
                    ) : null}
                </div>
            )}
        </>
    );
};

export default FiltersForm;